import { SESSION_IDLE_RESET_MS, SESSION_REMINDER_MS } from '@casino/shared';
import { audio } from '../audio/AudioManager.js';
import { formatDuration } from './anim.js';
import { createSheet, el } from './dom.js';
import { getSettings, updateSettings } from './settings.js';

/**
 * Garde-fou anti-addiction.
 *
 * Ce n'est pas une option decorative : le compteur de session est affiche en
 * permanence dans le bandeau joueur, et une invitation a la pause s'ouvre apres
 * SESSION_REMINDER_MS de jeu continu. Une inactivite de SESSION_IDLE_RESET_MS
 * remet la session a zero — quelqu'un qui laisse l'onglet ouvert ne joue pas.
 *
 * Le rappel modal peut etre desactive dans les parametres ; le compteur, lui,
 * ne se cache jamais.
 */

type Listener = (elapsedMs: number) => void;

const TICK_MS = 1000;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

class SessionGuard {
  #startedAt = Date.now();
  #lastActivity = Date.now();
  #timer: number | null = null;
  #reminderOpen = false;
  #installed = false;
  readonly #listeners = new Set<Listener>();

  get elapsedMs(): number {
    return Date.now() - this.#startedAt;
  }

  start(): void {
    if (this.#installed) return;
    this.#installed = true;
    this.#startedAt = Date.now();
    this.#lastActivity = Date.now();

    const markActivity = (): void => {
      this.#lastActivity = Date.now();
    };
    for (const type of ACTIVITY_EVENTS) {
      window.addEventListener(type, markActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') markActivity();
    });

    this.#timer = window.setInterval(() => this.#tick(), TICK_MS);
    this.#emit();
  }

  stop(): void {
    if (this.#timer !== null) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  subscribe(listener: Listener): () => void {
    this.#listeners.add(listener);
    listener(this.elapsedMs);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  /** Remet le compteur a zero (pause acceptee, ou inactivite prolongee). */
  reset(): void {
    this.#startedAt = Date.now();
    this.#lastActivity = Date.now();
    this.#emit();
  }

  #tick(): void {
    const now = Date.now();

    if (now - this.#lastActivity >= SESSION_IDLE_RESET_MS) {
      this.#startedAt = now;
      this.#lastActivity = now;
      this.#emit();
      return;
    }

    if (
      !this.#reminderOpen &&
      getSettings().sessionReminder &&
      now - this.#startedAt >= SESSION_REMINDER_MS
    ) {
      this.#openReminder();
    }

    this.#emit();
  }

  #emit(): void {
    const elapsed = this.elapsedMs;
    for (const listener of [...this.#listeners]) listener(elapsed);
  }

  #openReminder(): void {
    this.#reminderOpen = true;
    const played = this.elapsedMs;
    audio.play('whoosh');

    const sheet = createSheet({
      title: 'Une pause ?',
      subtitle: `Tu joues depuis ${formatDuration(played)} sans interruption.`,
      variant: 'modal',
      dismissible: false,
      onClose: () => {
        this.#reminderOpen = false;
        this.reset();
      },
    });

    sheet.body.appendChild(
      el('div', { class: 'stack' }, [
        el('p', { class: 'reminder__lead' }, [
          'Le salon ne ferme pas. Les jetons sont virtuels, ils ne valent rien et ne ',
          "s'achètent pas : rien ici ne mérite d'y passer la nuit.",
        ]),
        el('ul', { class: 'reminder__list' }, [
          el('li', { text: 'Lève-toi, bois un verre d’eau, regarde par la fenêtre.' }),
          el('li', { text: 'Reviens plus tard : ta progression ne bouge pas.' }),
          el('li', { text: 'Le compteur repart à zéro après cette pause.' }),
        ]),
        el('div', { class: 'reminder__actions' }, [
          el('button', {
            class: 'button button--ghost',
            text: 'Couper le son et faire une pause',
            attrs: { type: 'button' },
            on: {
              click: () => {
                updateSettings({ muted: true });
                sheet.close();
              },
            },
          }),
          el('button', {
            class: 'button button--primary',
            text: "J'ai compris",
            attrs: { type: 'button' },
            on: {
              click: () => {
                audio.play('click');
                sheet.close();
              },
            },
          }),
        ]),
      ]),
    );
  }
}

export const sessionGuard = new SessionGuard();
